import { Box, InputLabel, makeStyles, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Portal from 'portal/Portal';
import Image from 'components/Image';
import Table from 'components/Table';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import fieldSelection from '..';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import PopUp from 'components/Operational/PopUp/PopUp';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(() => ({
  formContainer: {
    marginBottom: '1rem',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: '0.5rem',
  },
  img: {
    padding: '0.5rem',
    boxSizing: 'border-box',
  },
  EventForm: {
    display: 'flex',
    height: '85vh',
    width: '70vw',
    position: 'relative',
    transform: 'translate(10%, 0%)',
  },
}));

const EmbeddedFormFollowup = ({
  item,
  formProps,
  className,
  componentProps,
  additionalFields,
  archiveDelete,
  setFormikFieldValues,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editModeValues, setEditModeValues] = useState(null);
  const [apiValues, setApiValues] = useState(null);
  const [popupCheck, setPopupCheck] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();

  const closeForm = () => {
    setShowForm(false);
    setApiValues(null);
    setEditModeValues(null);
  };

  const additionalCompProps = {
    ...componentProps,
    ...(apiValues && { [item.dataFieldKey]: { ...apiValues } }), // only add api values if available
  };

  const toggleForm = async () => {
    // need to make an api call first before opening the form
    if (item.fetchApi && item.enableApiCall) {
      const { data } = await item.fetchApi({
        ...item.fetchPayload,
      });

      if (data) {
        item.data(data.data);
        setApiValues(data.data);
        setShowForm(!showForm);
      }
    } else {
      setShowForm(!showForm);
    }
  };

  const onDelete = val => {
    const values = formProps.values[item.field];
    let afterDelete = [];
    if (item.field === 'followupEvents') {
      afterDelete = values.filter(item => item.general.id !== val.general.id);
    }
    if (item.field === 'followUptask') {
      afterDelete = values.filter(item => item.rootTaskId !== val.rootTaskId);
    }
    formProps.setFieldValue(item.field, afterDelete);
    // update value in updatedValues so that it can display correct entities
    item.updatedValues[item.field] = afterDelete;
    const deletedValues = formProps.values[`deleted${item.field}`] || [];
    formProps.setFieldValue(
      `deleted${item.field}`,
      deletedValues.concat({
        ...val,
        ...(archiveDelete && { archived: true }),
      }),
    );
  };

  const onEdit = data => {
    toggleForm();
    setEditModeValues(data);
  };

  const submitBtnHandler = e => {
    e.preventDefault();
    setFormikFieldValues(item.setKeyValues);
    setPopupCheck(false);
    // the user wants to proceed so toggle form
    toggleForm();
  };

  const closePopupHandler = e => {
    e.preventDefault();
    setPopupCheck(false);
  };

  const checkBeforeToggle = () => {
    if (item.popUpRequired) {
      setPopupCheck(true);
    } else {
      toggleForm();
    }
  };

  let subFormValues = [];
  if (item?.updatedValues) {
    subFormValues = item.updatedValues[item.field];
    // update value in formik state if not empty
    if (!isEmpty(subFormValues)) {
      formProps.values[item.field] = subFormValues;
    }
  } else {
    subFormValues = formProps.values[item.field];
  }

  const hasSubFormValues = subFormValues && subFormValues.length > 0;
  return (
    <div className={cn(classes.formContainer, className)}>
      <div className={classes.labelContainer}>
        <InputLabel shrink required={item.required}>
          {t(item.label)}
          {item.info && <Tooltip text={item.info} />}
        </InputLabel>
        <Image
          title={t(item.label)}
          text={`${t('Add')} ${t(item.label)}`}
          icon={<AddCircleOutlineSharpIcon />}
          onClick={checkBeforeToggle}
          className={classes.img}
          disabled={item.disabled}
        />
      </div>
      {additionalFields &&
        additionalFields.map((field, key) => {
          if (field.condition && typeof field.condition === 'function') {
            return field.condition(formProps.values)
              ? fieldSelection({ item: field, key, props: formProps })
              : null;
          }
          return fieldSelection({ item: field, key, props: formProps });
        })}
      {hasSubFormValues && !showForm && !item?.hideTable && (
        <Table
          allowEditRow
          allowDeleteRow
          columns={item.columns}
          data={
            item.dataTransform && subFormValues
              ? item.dataTransform(subFormValues)
              : subFormValues
          }
          onDelete={onDelete}
          onEdit={onEdit}
        />
      )}
      {showForm && (
        <Portal id="portal">
          <Box component={'div'} className={classes.EventForm}>
            <item.component
              editMode={!isEmpty(editModeValues)}
              editModeValues={editModeValues}
              defaultValues={item.defaultValues}
              closeForm={closeForm}
              formProps={formProps}
              formItem={item}
              open={showForm}
              {...additionalCompProps}
            />
          </Box>
        </Portal>
      )}
      {popupCheck &&
      item.popUpRequired && ( // check the condition on which i need to show the popup, -> that requires the assigned asset value
          <PopUp
            title={
              item.popupMessages ? item.popupMessages.popupTitle : 'popup_title'
            }
            statusMessage={
              item.popupMessages
                ? item.popupMessages.popupStatusMessage
                : 'It is in use.'
            }
            submitBtnText={
              item.popupMessages
                ? item.popupMessages.submitBtnText
                : 'yes_action'
            }
            submit={submitBtnHandler}
            cancelBtnText={
              item.popupMessages ? item.popupMessages.cancelBtnText : 'No'
            }
            cancel={closePopupHandler}
            close={closePopupHandler}
          />
        )}
    </div>
  );
};

EmbeddedFormFollowup.propTypes = {
  item: PropTypes.object,
  formProps: PropTypes.object,
  className: PropTypes.string,
  component: PropTypes.object,
  componentProps: PropTypes.object,
  additionalFields: PropTypes.array,
  setFormikFieldValues: PropTypes.func,
  archiveDelete: PropTypes.bool,
};

export default EmbeddedFormFollowup;
